import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Grid3X3,
  Kanban,
  RefreshCw,
  AlertCircle,
  Target,
  Calendar,
  Users,
  Clock,
  Tag
} from 'lucide-react';
import { KanbanBoard } from '@/components/kanban-board';
import { getTagColors } from '@/lib/tag-colors';
import { getStatusColors } from '@/lib/status-colors';
import { useOptionalProductionManagement } from '@/lib/production-management-context';
import { format } from 'date-fns';
import { fetchUserTasksFromWP, clearUserTasksCache } from '@/lib/notion-client';
import type { Task } from '@/lib/types';

interface TaskStats {
  totalTasks: number;
  tasksByStatus: Record<string, number>;
  completionRate: number;
}

export default function TasksPage() {
  const navigate = useNavigate();
  const productionContext = useOptionalProductionManagement();
  const getUsersByEmails = productionContext?.getUsersByEmails;
  
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"kanban" | "grid">("kanban");
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Helper function to get assignee names from emails
  const getAssigneeNames = (assigneeEmails?: string[]): string => {
    if (!assigneeEmails || assigneeEmails.length === 0) return ""
    if (getUsersByEmails) {
      const users = getUsersByEmails(assigneeEmails)
      if (users.length > 0) return users.map(u => u.name).join(", ")
    }
    return `${assigneeEmails.length} assignee${assigneeEmails.length !== 1 ? "s" : ""}`
  }

  const fetchTasks = async (force: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const wp = (window as any).WP_COCKPIT;
      const isDev = wp?.devMode || false;
      
      if (isDev) {
        console.log('[TasksPage] Fetching user tasks, force refresh:', force);
      }
      
      // Clear cache for hard refresh
      if (force) {
        clearUserTasksCache();
        if (isDev) console.log('[TasksPage] Cache cleared for hard refresh');
      }
      
      const userTasks = await fetchUserTasksFromWP({ force });
      
      if (isDev) {
        console.log('[TasksPage] Received user tasks:', userTasks);
        console.log('[TasksPage] Task statuses found:', [...new Set(userTasks.map(t => t.status))]);
      }
      
      setTasks(userTasks);
    } catch (err) {
      console.error('Error fetching user tasks:', err);
      setError('Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const calculateTaskStats = (): TaskStats => {
    // Define status groups for stats calculation (matching the task kanban order)
    const statusGroups = {
      "To-do": ["Planned", "Backlog"],
      "In Progress": ["Quality Check", "In Progress", "Maintenance"], 
      "Completed": ["Done", "Archived"]
    };

    const tasksByStatus = {
      "To-do": tasks.filter(t => t.status && statusGroups["To-do"].includes(t.status)).length,
      "In Progress": tasks.filter(t => t.status && statusGroups["In Progress"].includes(t.status)).length,
      "Completed": tasks.filter(t => t.status && statusGroups["Completed"].includes(t.status)).length,
    };

    const totalTasks = tasks.length;
    const completedTasks = tasksByStatus["Completed"];
    const completionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

    return {
      totalTasks,
      tasksByStatus,
      completionRate,
    };
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const handleTaskSelect = (task: Task | null) => {
    if (task) {
      // Navigate to TaskPage with task data in state
      navigate(`/task/${task.id}`, { state: { task } });
    } else {
      setSelectedTask(null);
    }
  };

  const stats = calculateTaskStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-6"></div>
            <div className="grid gap-6 md:grid-cols-3">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button 
            onClick={() => fetchTasks(true)} 
            variant="outline" 
            className="mt-4"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Target className="h-8 w-8 text-blue-600" />
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  My Tasks
                </h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                Manage and track your personal tasks
              </p>
            </div>
            
            <Button onClick={() => fetchTasks(true)}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Total Tasks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalTasks}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">To-do</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.tasksByStatus["To-do"]}</div>
              <p className="text-xs text-muted-foreground">
                {stats.totalTasks > 0 ? ((stats.tasksByStatus["To-do"] / stats.totalTasks) * 100).toFixed(1) : 0}%
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.tasksByStatus["In Progress"]}</div>
              <p className="text-xs text-muted-foreground">
                {stats.totalTasks > 0 ? ((stats.tasksByStatus["In Progress"] / stats.totalTasks) * 100).toFixed(1) : 0}%
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.tasksByStatus["Completed"]}</div>
              <p className="text-xs text-muted-foreground">
                {stats.totalTasks > 0 ? ((stats.tasksByStatus["Completed"] / stats.totalTasks) * 100).toFixed(1) : 0}%
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tasks Section */}
        <div className="space-y-6">
          {/* Tasks Header with View Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Your Tasks</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {stats.totalTasks} tasks • {stats.tasksByStatus["Completed"]} completed
              </p>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <Button
                variant={viewMode === "kanban" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("kanban")}
                className="h-8 px-3"
              >
                <Kanban className="h-4 w-4 mr-1" />
                Kanban
              </Button>
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
                className="h-8 px-3"
              >
                <Grid3X3 className="h-4 w-4 mr-1" />
                Grid
              </Button>
            </div>
          </div>

          {/* Tasks Content */}
          {viewMode === "kanban" ? (
            <KanbanBoard 
              tasks={tasks} 
              selectedTask={selectedTask}
              onSelectTask={handleTaskSelect}
              enableNavigation={true}
            />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {tasks.map((task) => (
                <Card 
                  key={task.id} 
                  className={`border-l-4 cursor-pointer hover:shadow-lg transition-shadow ${
                    selectedTask?.id === task.id ? 'ring-2 ring-blue-500' : ''
                  }`}
                  style={{ borderLeftColor: getStatusColors(task.status).borderColorValue }}
                  onClick={() => handleTaskSelect(task)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <CardTitle className="text-lg">{task.title}</CardTitle>
                      <Badge className={`${getStatusColors(task.status).bg} ${getStatusColors(task.status).text} ${getStatusColors(task.status).border} border`}>
                        {task.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      {task.due && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span>Due: {formatDate(task.due.toString())}</span>
                        </div>
                      )}
                      {(task.estimatedHours !== undefined || task.realisedHours !== undefined) && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          {task.realisedHours !== undefined && task.estimatedHours !== undefined ? (
                            <span>{task.realisedHours.toFixed(1)}h / {task.estimatedHours}h</span>
                          ) : task.realisedHours !== undefined ? (
                            <span>{task.realisedHours.toFixed(1)}h tracked</span>
                          ) : (
                            <span>Est: {task.estimatedHours}h</span>
                          )}
                        </div>
                      )}
                      {task.assignee && task.assignee.length > 0 && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Users className="h-4 w-4" />
                          <span className="font-medium">{getAssigneeNames(task.assignee)}</span>
                        </div>
                      )}
                      {task.contributors && task.contributors.length > 0 && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Users className="h-4 w-4" />
                          <span>{task.contributors.length} contributor{task.contributors.length > 1 ? 's' : ''}</span>
                        </div>
                      )}
                      {task.tags && task.tags.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Tag className="h-4 w-4 text-muted-foreground" />
                          <div className="flex gap-1 flex-wrap">
                            {task.tags.slice(0, 3).map((tag, index) => {
                              const tagColors = getTagColors(tag);
                              return (
                                <Badge key={index} className={`text-xs ${tagColors.bg} ${tagColors.text} ${tagColors.border} border`}>
                                  {tag}
                                </Badge>
                              );
                            })}
                            {task.tags.length > 3 && (
                              <Badge variant="secondary" className="text-xs">
                                +{task.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                      {task.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-2">
                          {task.description}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {tasks.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Target className="h-12 w-12 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
                <p className="text-gray-500 dark:text-gray-400">No tasks found</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                  Tasks will appear here once they are created and assigned to you.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}